import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { MOIS, JOURS_COURTS, monthGrid, todayISO, sessionStatus } from "../../lib/format";
import { STATUS_STYLES } from "../common";

// Calendrier mensuel des cours : chaque cours est coloré selon qu'il est
// passé, aujourd'hui ou à venir. Cliquer un jour appelle onSelect(iso).
export function MonthCalendar({ year, month, onMonthChange, sessions = [], selected, onSelect, maxPerDay = 3 }) {
  const days = useMemo(() => monthGrid(year, month), [year, month]);
  const today = todayISO();
  const byDay = useMemo(() => {
    const map = {};
    sessions.forEach((s) => { (map[s.date] = map[s.date] || []).push(s); });
    Object.values(map).forEach((l) => l.sort((a, b) => a.heure_debut.localeCompare(b.heure_debut)));
    return map;
  }, [sessions]);

  const go = (delta) => {
    const d = new Date(year, month + delta, 1);
    onMonthChange(d.getFullYear(), d.getMonth());
  };
  const goToday = () => {
    const d = new Date();
    onMonthChange(d.getFullYear(), d.getMonth());
    onSelect && onSelect(todayISO());
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="month-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => go(-1)} aria-label="Mois précédent" data-testid="cal-prev"><ChevronLeft /></Button>
          <h2 className="font-heading text-lg font-semibold capitalize min-w-[150px] text-center">{MOIS[month]} {year}</h2>
          <Button variant="ghost" size="icon" onClick={() => go(1)} aria-label="Mois suivant" data-testid="cal-next"><ChevronRight /></Button>
          <Button variant="outline" size="sm" onClick={goToday} className="ml-1">Aujourd'hui</Button>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {["passe", "aujourdhui", "futur"].map((k) => (
            <span key={k} className="flex items-center gap-1.5"><span className={cn("h-2 w-2 rounded-full", STATUS_STYLES[k].dot)} />{k === "passe" ? "Passés" : k === "aujourdhui" ? "Aujourd'hui" : "À venir"}</span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-border bg-muted/40">
        {JOURS_COURTS.map((j) => (
          <div key={j} className="px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground text-center">{j}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const list = byDay[d.iso] || [];
          const isToday = d.iso === today;
          const isSel = d.iso === selected;
          const status = sessionStatus(d.iso);
          return (
            <button
              type="button"
              key={d.iso}
              onClick={() => onSelect && onSelect(d.iso)}
              data-testid={`cal-day-${d.iso}`}
              className={cn(
                "relative min-h-[76px] sm:min-h-[104px] text-left p-1.5 sm:p-2 border-border transition-colors",
                i % 7 !== 6 && "border-r",
                i < days.length - 7 && "border-b",
                !d.inMonth && "bg-muted/30",
                "hover:bg-accent/60",
                isSel && "bg-primary/5 ring-2 ring-inset ring-primary",
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs font-medium tabular-nums",
                    isToday ? "bg-primary text-primary-foreground" : d.inMonth ? "text-foreground" : "text-muted-foreground/60",
                  )}
                >
                  {d.day}
                </span>
                {list.length > 0 && <span className="sm:hidden text-[10px] font-semibold text-muted-foreground">{list.length}</span>}
              </div>
              {/* Mobile : simples pastilles ; écran large : libellés des cours */}
              <div className="mt-1 flex flex-wrap gap-1 sm:hidden">
                {list.slice(0, 4).map((s) => <span key={s.id} className={cn("h-1.5 w-1.5 rounded-full", STATUS_STYLES[status].dot)} />)}
              </div>
              <div className="mt-1 space-y-1 hidden sm:block">
                {list.slice(0, maxPerDay).map((s) => (
                  <div key={s.id} className={cn("truncate rounded border-l-2 px-1.5 py-0.5 text-[11px] leading-tight", STATUS_STYLES[status].block)}>
                    <span className="font-medium tabular-nums">{s.heure_debut}</span> {s.module_nom}
                  </div>
                ))}
                {list.length > maxPerDay && <div className="text-[11px] text-muted-foreground pl-1">+{list.length - maxPerDay} autre{list.length - maxPerDay > 1 ? "s" : ""}</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
